import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { Button } from "semantic-ui-react";

import styled from "styled-components";
import { OpenAPI } from "simplydo/interfaces";
import { ProjectSelectionModes } from "../.";
import { DndContext } from "components/lib/dnd/DndContext";
import { Droppable } from "components/lib/dnd/Droppable";
import { Draggable } from "components/lib/dnd/Draggable";
import { mapMoveables, moveItem, MoveResult } from "components/lib/dnd/dndHelpers";
import { closestCorners } from "@dnd-kit/core";

export type IIdeaCard = {
  id: string;
  idea: OpenAPI.Schemas["Idea"];
  lane?: string;
  position?: number;
  laneMetaType?: OpenAPI.Schemas["projectLanes"]["metaType"];
  draggingDisabled?: boolean;
};

export type ILaneData = {
  id: string;
  title: string;
  label?: string;
  description?: string;
  metaType?: string;
  cards: IIdeaCard[];
  draggingDisabled?: boolean;
};

type BoardProps = {
  lanes: ILaneData[];
  setLanes: Dispatch<SetStateAction<ILaneData[]>>;
  highlightCard?: string;
  renderCard: (card: ILaneData["cards"][0]) => JSX.Element;
  renderLaneHeader?: (lane: ILaneData) => JSX.Element;
  draggable?: boolean;
  onCardMove?: (event: MoveResult<IIdeaCard>) => void;
  onLaneMove?: (event: MoveResult<ILaneData>) => void;
  createLane?: () => void;
  projectSelectionMode?: ProjectSelectionModes;
};

const BoardWrapper = styled.div`
  display: flex;
`;

const BoardOverflow = styled.div`
  overflow: auto;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  flex: 1;
  width: 100%;
  height: 100%;
  gap: 12px;
  padding: 2px;
  padding-bottom: 20px;
`;

const LaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 300px;
  width: 300px;
  margin: 0 10px;
  &:first-child {
    margin-left: 0;
  }
  &:last-child {
    margin-right: 0;
  }
  max-height: 100%;
  padding: 8px;

  .lane-content {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    gap: 8px;

    ::-webkit-scrollbar {
      width: 7px;
    }
    ::-webkit-scrollbar-thumb {
      border-radius: 5px; /* Add border-radius for a rounded thumb */
    }
    ::-webkit-scrollbar-track {
      background: rgb(230, 231, 233);
    }
  }

  border-radius: 5px;
  background: rgb(241, 242, 244);
  box-shadow:
    rgba(9, 30, 66, 0.25) 0px 1px 1px 0px,
    rgba(9, 30, 66, 0.31) 0px 0px 1px 0px;
`;

const NewLaneWrapper = styled(LaneWrapper)`
  padding: 0;
  .ui.button {
    border: 0;
    margin: 0;
    box-shadow: none;
  }
`;

const CardWrapper = styled.div`
  cursor: pointer;
  z-index: 1;
`;

const Board = ({ lanes, setLanes, renderCard, renderLaneHeader, createLane, onLaneMove, onCardMove }: BoardProps) => {
  const isDraggingBoard = useRef(false);
  const isUsingDnd = useRef(false);

  const boardRef = useRef(null);
  const highlightRef = useRef(null);

  useEffect(() => {
    if (boardRef.current) {
      boardRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    if (highlightRef.current) {
      highlightRef.current.scrollIntoView({
        behavior: "smooth",
        inline: "center",
      });
    }
  }, []);

  useEffect(() => {
    const draggableBoard = document.getElementsByClassName("board-overflow").item(0) as HTMLDivElement;
    if (!draggableBoard) {
      return;
    }

    const mouseMoveListener = (e: MouseEvent) => {
      if (isDraggingBoard.current && !isUsingDnd.current) {
        draggableBoard.scrollLeft -= e.movementX;
      }
    };
    const mouseUpListener = (_evt) => {
      // Always release the board or card if a mouseup occurs anywhere on the document
      isDraggingBoard.current = false;
      isUsingDnd.current = false;
    };

    document.addEventListener("mouseup", mouseUpListener);
    document.addEventListener("mousemove", mouseMoveListener);
    return () => {
      document.removeEventListener("mousemove", mouseMoveListener);
      document.removeEventListener("mouseup", mouseUpListener);
    };
  }, []);

  useEffect(() => {}, []);

  return (
    <DndContext
      collisionDetection={closestCorners}
      onDragStart={() => {
        isUsingDnd.current = true;
      }}
      onDragEnd={(event, dropInfo) => {
        // Handle lane moves
        const laneMove = moveItem(
          event,
          {
            board: {
              items: lanes,
              statefulSetter: setLanes,
            },
          },
          {
            canMoveItem: ({ fromIndex, toIndex }) => fromIndex !== 0 && toIndex !== 0,
          },
        );
        if (laneMove.didMove) {
          onLaneMove?.(laneMove);
          return;
        }

        // Or handle card moves
        const cardMove = moveItem(
          event,
          mapMoveables(lanes, (lane) => ({
            droppableId: `lane-${lane.id}`,
            items: lane.cards,
            directSetter: (cards) => {
              setLanes((prev) => {
                const newState = [...prev];
                const laneIndex = newState.findIndex((l) => l.id === lane.id);
                newState[laneIndex].cards = cards;
                return newState;
              });
            },
          })),
        );
        if (cardMove.didMove || dropInfo.originalDroppableId !== cardMove.toDroppable) {
          onCardMove?.(cardMove);
          return;
        }
      }}
      onDragOver={(event) => {
        // Handle lane moves
        moveItem(
          event,
          mapMoveables(lanes, (lane) => ({
            droppableId: `lane-${lane.id}`,
            items: lane.cards,
            directSetter: (cards) => {
              setLanes((prev) => {
                const newState = [...prev];
                const laneIndex = newState.findIndex((l) => l.id === lane.id);
                newState[laneIndex].cards = cards;
                return newState;
              });
            },
          })),
          {
            restrictMovesTo: "differentDroppableOnly",
          },
        );
      }}
    >
      <Droppable id="board" direction="horizontal" itemIds={lanes.map((lane) => `lane-${lane.id}`)}>
        <BoardWrapper className="board-wrapper" ref={boardRef}>
          <BoardOverflow
            className="board-overflow"
            onMouseDown={() => {
              isDraggingBoard.current = true;
            }}
          >
            {lanes.map((lane) => {
              return (
                <Draggable disabled={lane.draggingDisabled} key={lane.id} id={`lane-${lane.id}`}>
                  <LaneWrapper>
                    {renderLaneHeader ? renderLaneHeader(lane) : null}

                    <Droppable
                      id={`lane-${lane.id}`}
                      direction="vertical"
                      itemIds={lane.cards.map((card) => `card-${card.id}`)}
                      emptyAreaStyle={{ height: 100 }}
                    >
                      <div className="lane-content">
                        {lane.cards.map((card) => (
                          <Draggable key={card.id} id={`card-${card.id}`} disabled={card.draggingDisabled}>
                            <CardWrapper ref={highlightRef}>{renderCard(card)}</CardWrapper>
                          </Draggable>
                        ))}
                      </div>
                    </Droppable>
                  </LaneWrapper>
                </Draggable>
              );
            })}
            {createLane ? (
              <NewLaneWrapper>
                <Button basic content="Create a new lane" icon="plus" onClick={createLane} />
              </NewLaneWrapper>
            ) : null}
          </BoardOverflow>
        </BoardWrapper>
      </Droppable>
    </DndContext>
  );
};

export default Board;
